import { useClickOutside, RichText, htmlToSlate, slateToHtml } from '@amelio/ui-library';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, PenIcon, PlusIcon, SparklesIcon, XIcon } from '../PrepareMeetingIcons';
import { HandoutSectionTitle } from './HandoutSectionTitle';

import type {
    AiRecommendationPromptModalRef,
    RecommendationIntegrationMode,
} from '@/components/AiRecommendationPromptModal/AiRecommendationPromptModal';
import type { HandoutPredefinedSectionData } from '@/types/review/ReviewDiscussion';
import type { Descendant } from 'slate';

import { AiRecommendationPromptModal } from '@/components/AiRecommendationPromptModal/AiRecommendationPromptModal';

interface HandoutPredefinedSectionsProps {
    predefinedSection?: HandoutPredefinedSectionData[];
    predefinedSectionKey?: string;
    onPredefinedSectionSave?: (sections: HandoutPredefinedSectionData[]) => void;
    /**
     * Generates the recommendations text from the manager's two modal answers, resolving to ready-to-store
     * HTML (or null on failure). When omitted, the "Regenerate with AI" affordance is hidden.
     */
    onRecommendationsGenerate?: (feeling: string, priority: string) => Promise<string | null>;
    isSaving?: boolean;
    isSummaryPage?: boolean;
}

type PresetSections = Record<string, { title: string }>;

// The RichText editor represents an "empty" section as markup (e.g. "<p></p>"), so a raw string check would
// wrongly report content. Strip tags and entities to decide whether there's any real text to append to.
const htmlHasText = (html?: string): boolean =>
    Boolean(
        html
            ?.replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .trim(),
    );

export const HandoutPredefinedSections = ({
    predefinedSection,
    predefinedSectionKey,
    onPredefinedSectionSave,
    onRecommendationsGenerate,
    isSaving,
    isSummaryPage = true,
}: HandoutPredefinedSectionsProps) => {
    const { t, i18n } = useTranslation();
    const presetSections = t('postReviewPrepareMeeting.notes.presetSections', {
        returnObjects: true,
    }) as PresetSections;

    const [localSections, setLocalSections] = useState<HandoutPredefinedSectionData[]>(
        () => predefinedSection ?? [],
    );
    const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [regenerateKeys, setRegenerateKeys] = useState<Record<string, number>>({});
    const [regeneratingSectionId, setRegeneratingSectionId] = useState<string | null>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<AiRecommendationPromptModalRef>(null);

    useClickOutside(popoverRef, () => setIsPopoverOpen(false), isPopoverOpen);

    useEffect(() => {
        setLocalSections(predefinedSection ?? []);
        setEditingIds(new Set());
    }, [predefinedSectionKey]);

    const addedPresetKeys = useMemo(
        () =>
            new Set(
                localSections
                    .map((section) => section.presetKey)
                    .filter((presetKey): presetKey is string => Boolean(presetKey)),
            ),
        [localSections],
    );

    const presetSectionKeys = useMemo(() => Object.keys(presetSections), [presetSections]);

    const isAddButtonDisabled = useMemo(
        () => presetSectionKeys.length > 0 && addedPresetKeys.size === presetSectionKeys.length,
        [addedPresetKeys, presetSectionKeys],
    );

    const handleAddSection = (presetKey: string) => {
        if (addedPresetKeys.has(presetKey)) return;

        const preset = presetSections[presetKey];
        if (!preset) return;

        const newSection: HandoutPredefinedSectionData = {
            id: window.crypto.randomUUID(),
            presetKey,
            title: preset.title,
            content: '',
        };

        setLocalSections((previous) => {
            const focusIndex = previous.findIndex((section) => section.presetKey === 'focus');
            const insertIndex = focusIndex !== -1 ? focusIndex + 1 : previous.length;
            const next = [
                ...previous.slice(0, insertIndex),
                newSection,
                ...previous.slice(insertIndex),
            ];
            onPredefinedSectionSave?.(next);
            return next;
        });
        setEditingIds((previous) => new Set(previous).add(newSection.id));

        if (addedPresetKeys.size + 1 === presetSectionKeys.length) {
            setIsPopoverOpen(false);
        }
    };

    const handleDeleteSection = (id: string) => {
        setLocalSections((previous) => {
            const next = previous.filter((section) => section.id !== id);
            onPredefinedSectionSave?.(next);
            return next;
        });

        setEditingIds((previous) => {
            const next = new Set(previous);
            next.delete(id);
            return next;
        });
    };

    const handleSectionContentChange = (id: string, value: Descendant[]) => {
        setLocalSections((previous) =>
            previous.map((section) =>
                section.id === id ? { ...section, content: slateToHtml(value) } : section,
            ),
        );
    };

    const handleRegenerateAi = useCallback((id: string) => {
        setRegeneratingSectionId(id);
        modalRef.current?.open();
    }, []);

    // Integrate the generated HTML into the recommendations section: 'replace' overwrites, 'add' appends to
    // the existing content. The RichText for the section is remounted (regenerateKeys) so it picks up the new
    // value, and the change is persisted.
    const handleModalIntegrate = useCallback(
        (mode: RecommendationIntegrationMode, generatedHtml: string) => {
            if (!regeneratingSectionId) return;

            setLocalSections((previous) => {
                const next = previous.map((section) => {
                    if (section.id !== regeneratingSectionId) return section;
                    const content =
                        mode === 'add' && section.content
                            ? `${section.content}${generatedHtml}`
                            : generatedHtml;
                    return { ...section, content };
                });
                onPredefinedSectionSave?.(next);
                return next;
            });
            setRegenerateKeys((previous) => ({
                ...previous,
                [regeneratingSectionId]: (previous[regeneratingSectionId] ?? 0) + 1,
            }));
            setRegeneratingSectionId(null);
        },
        [regeneratingSectionId, onPredefinedSectionSave],
    );

    const handleSave = (id: string) => {
        onPredefinedSectionSave?.(localSections);
        setEditingIds((previous) => {
            const next = new Set(previous);
            next.delete(id);
            return next;
        });
    };

    const handleEdit = (id: string) => {
        setEditingIds((previous) => new Set(previous).add(id));
    };

    return (
        <div className={'space-y-4'}>
            {isSummaryPage &&
                localSections.map((section) => {
                    const isEditing = editingIds.has(section.id);
                    const title = section.presetKey
                        ? (presetSections[section.presetKey]?.title ?? section.title)
                        : section.title;
                    const isRecommendations = section.presetKey === 'recommendations';

                    return (
                        <div
                            className={
                                'ds-p-lg rounded-xl border border-gray-100 bg-slate-100 shadow-xs'
                            }
                            key={section.id}
                        >
                            {isEditing ? (
                                <>
                                    <div
                                        className={
                                            'flex flex-col gap-2 md:flex-row md:items-center md:justify-between'
                                        }
                                    >
                                        <HandoutSectionTitle>{title}</HandoutSectionTitle>
                                        {isRecommendations && onRecommendationsGenerate && (
                                            <button
                                                type={'button'}
                                                onClick={() => handleRegenerateAi(section.id)}
                                                className={
                                                    'flex items-center gap-2 rounded-lg border border-[#8751ec] px-4 py-2 text-sm font-semibold text-[#8751ec] transition hover:bg-[#8751ec]/10 disabled:cursor-not-allowed disabled:opacity-60'
                                                }
                                            >
                                                <SparklesIcon
                                                    className={'h-3.5 w-3.5 text-[#8751ec]'}
                                                />
                                                {t(
                                                    'postReviewPrepareMeeting.recommendations.regenerateWithAI',
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    <RichText
                                        key={`${section.id}-${regenerateKeys[section.id] ?? 0}`}
                                        value={htmlToSlate(section.content || '')}
                                        onChange={(value) =>
                                            handleSectionContentChange(section.id, value)
                                        }
                                        placeholder={t(
                                            'postReviewPrepareMeeting.notes.sectionPlaceholder',
                                        )}
                                        maxLength={5000}
                                        showCharacterCount={true}
                                        locale={i18n.language}
                                        className={
                                            '!p-0 md:!p-[15px] [&_p]:mb-0 [&>div>div[role=toolbar]]:!mx-0 md:[&>div>div[role=toolbar]]:!-mx-2'
                                        }
                                        toolbarOptions={{
                                            headingOne: true,
                                            headingTwo: true,
                                            bold: true,
                                            italic: true,
                                            underline: true,
                                            strikethrough: true,
                                            link: true,
                                            bulletList: true,
                                            numberedList: true,
                                        }}
                                    />
                                    <div className={'mt-6 flex justify-end gap-2 sm:mt-4'}>
                                        <button
                                            type={'button'}
                                            onClick={() => handleDeleteSection(section.id)}
                                            disabled={isSaving}
                                            className={
                                                'text-body-sm! flex items-center gap-2 rounded-xl border border-red-500/30 bg-white px-[12px] py-[6px] font-medium text-[#ef4444] transition hover:bg-[#ef4444]/10 disabled:cursor-not-allowed disabled:opacity-60'
                                            }
                                        >
                                            <XIcon className={'h-4 w-4'} />
                                            {t('postReviewPrepareMeeting.notes.deleteSection')}
                                        </button>
                                        <button
                                            type={'button'}
                                            onClick={() => handleSave(section.id)}
                                            disabled={isSaving}
                                            className={
                                                'text-md bg-primary flex items-center gap-2 rounded-xl px-[12px] py-[6px] text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-80'
                                            }
                                        >
                                            <CheckIcon className={'h-4 w-4'} />
                                            {t('postReviewPrepareMeeting.notes.saveSection')}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div
                                    data-pdf-block={'true'}
                                    className={'group relative break-inside-avoid'}
                                >
                                    <HandoutSectionTitle className={'mb-2'}>
                                        {title}
                                    </HandoutSectionTitle>
                                    {section.content ? (
                                        <div
                                            className={
                                                'min-h-6 text-sm [&_a]:text-blue-500 [&_a]:underline [&_em]:italic [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[20px] [&_h1]:leading-tight [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-[16px] [&_h2]:leading-snug [&_h2]:font-semibold [&_li]:ml-4 [&_ol]:my-4 [&_ol]:list-inside [&_ol]:list-decimal [&_ol]:space-y-2 [&_p]:mb-2 [&_s]:line-through [&_strong]:font-bold [&_u]:underline [&_ul]:my-4 [&_ul]:list-inside [&_ul]:list-disc [&_ul]:space-y-2'
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(section.content),
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className={
                                            'absolute top-0 right-0 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100'
                                        }
                                    >
                                        <button
                                            type={'button'}
                                            onClick={() => handleEdit(section.id)}
                                            className={
                                                'bg-primary hover:bg-primary/90 focus-visible:ring-primary-800 rounded-md p-1.5 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                                            }
                                            title={t('postReviewPrepareMeeting.notes.editSection')}
                                        >
                                            <PenIcon className={'h-3.5 w-3.5'} />
                                        </button>
                                        <button
                                            type={'button'}
                                            onClick={() => handleDeleteSection(section.id)}
                                            className={
                                                'bg-error hover:bg-error/90 focus-visible:ring-error rounded-md p-1.5 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                                            }
                                            title={t(
                                                'postReviewPrepareMeeting.notes.deleteSection',
                                            )}
                                        >
                                            <XIcon className={'h-3.5 w-3.5'} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

            <div
                ref={popoverRef}
                className={'absolute bottom-0 left-6 z-20'}
                data-pdf-hide={'true'}
            >
                <button
                    type={'button'}
                    aria-label={t('postReviewPrepareMeeting.notes.addSection')}
                    data-testid={'add-predefined-section'}
                    onClick={() => setIsPopoverOpen((previous) => !previous)}
                    disabled={isAddButtonDisabled}
                    className={
                        'group from-primary shadow-primary/15 ring-primary/20 focus-visible:ring-primary-800 flex h-11 w-11 items-center justify-center rounded-full bg-linear-to-br to-[#8751ec] text-white shadow-sm ring-1 transition-all duration-300 ease-out hover:scale-105 hover:shadow-xl hover:ring-0 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-sm disabled:hover:ring-1 disabled:hover:brightness-100'
                    }
                >
                    <PlusIcon
                        className={'h-5 w-5 transition-colors group-hover:text-white'}
                        strokeWidth={2.5}
                    />
                </button>

                {isPopoverOpen && (
                    <div
                        className={
                            'border-base-200 absolute bottom-full left-0 mb-2 w-72 rounded-lg border bg-white p-4 shadow-lg'
                        }
                    >
                        <h3 className={'text-body-sm mb-3 font-bold'}>
                            {t('postReviewPrepareMeeting.notes.addSection')}
                        </h3>
                        <div className={'space-y-2'}>
                            {Object.entries(presetSections).map(([key, section]) => {
                                const isAdded = addedPresetKeys.has(key);

                                return (
                                    <button
                                        key={key}
                                        type={'button'}
                                        disabled={isAdded}
                                        data-testid={`predefined-section-preset-${key}`}
                                        onClick={() => handleAddSection(key)}
                                        className={`w-full rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors ${
                                            isAdded
                                                ? 'bg-base-100 text-base-300 cursor-not-allowed'
                                                : 'bg-base-25 hover:bg-primary text-base hover:text-white'
                                        }`}
                                    >
                                        {isAdded ? `✓ ${section.title}` : section.title}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {onRecommendationsGenerate && (
                <AiRecommendationPromptModal
                    ref={modalRef}
                    onGenerate={onRecommendationsGenerate}
                    onIntegrate={handleModalIntegrate}
                    canAdd={htmlHasText(
                        localSections.find((section) => section.id === regeneratingSectionId)
                            ?.content,
                    )}
                />
            )}
        </div>
    );
};
